import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { UserPlus, Mail, Shield, X, Upload, Plus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface BulkInviteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  onInviteSuccess: () => void;
}

interface InvitationEntry {
  email: string;
  role: string;
  customMessage?: string;
}

const ROLES = [
  { value: 'viewer', label: 'Viewer', description: 'Can view data but cannot make changes' },
  { value: 'data_entry', label: 'Data Entry', description: 'Can view and enter data' },
  { value: 'coordinator', label: 'Coordinator', description: 'Can manage data and some settings' },
  { value: 'admin', label: 'Administrator', description: 'Full access to workspace management' },
];

export function BulkInviteModal({ open, onOpenChange, workspaceId, onInviteSuccess }: BulkInviteModalProps) {
  const [invitations, setInvitations] = useState<InvitationEntry[]>([{ email: '', role: 'viewer' }]);
  const [batchName, setBatchName] = useState('');
  const [globalMessage, setGlobalMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const addInvitation = () => {
    setInvitations([...invitations, { email: '', role: 'viewer' }]);
  };

  const removeInvitation = (index: number) => {
    setInvitations(invitations.filter((_, i) => i !== index));
  };

  const updateInvitation = (index: number, field: keyof InvitationEntry, value: string) => {
    const updated = [...invitations];
    updated[index] = { ...updated[index], [field]: value };
    setInvitations(updated);
  };

  const parseEmailList = (text: string) => {
    const emails = text
      .split(/[\n,;]/)
      .map(email => email.trim())
      .filter(email => email && email.includes('@'));
    
    const newInvitations = emails.map(email => ({ email, role: 'viewer' }));
    setInvitations(newInvitations);
  };

  const validateInvitations = () => {
    const validInvitations = invitations.filter(inv => inv.email.trim() && inv.role);
    
    if (validInvitations.length === 0) {
      toast({
        title: "No valid invitations",
        description: "Please add at least one valid email address",
        variant: "destructive",
      });
      return false;
    }

    const duplicateEmails = validInvitations
      .map(inv => inv.email.toLowerCase())
      .filter((email, index, arr) => arr.indexOf(email) !== index);
    
    if (duplicateEmails.length > 0) {
      toast({
        title: "Duplicate emails found",
        description: `Please remove duplicate emails: ${duplicateEmails.join(', ')}`,
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleBulkInvite = async () => {
    if (!validateInvitations()) return;

    setIsLoading(true);
    
    try {
      const validInvitations = invitations.filter(inv => inv.email.trim() && inv.role);
      
      console.log('🚀 Sending bulk invitations...', { 
        workspaceId, 
        count: validInvitations.length,
        batchName 
      });
      
      const response = await supabase.functions.invoke('workspace-invitations', {
        body: {
          action: 'bulk_create',
          workspace_id: workspaceId,
          invitations: validInvitations.map(inv => ({
            email: inv.email.trim(),
            role_code: inv.role,
            custom_message: inv.customMessage || globalMessage || undefined
          })),
          batch_name: batchName || `Bulk invite ${new Date().toLocaleDateString()}`
        }
      });
      
      const data = response.data;
      const error = response.error;

      console.log('📥 Bulk invitation response:', { data, error });

      if (error) {
        throw new Error(error.message || 'Failed to send bulk invitations');
      }
      
      if (data?.error) {
        throw new Error(data.error);
      }
      
      if (data?.ok && data?.action === 'bulk_create') {
        const { sent, failed, skipped, total } = data.data;
        
        toast({
          title: "Bulk invitations completed!",
          description: `${sent} sent, ${failed} failed, ${skipped} skipped out of ${total} total`,
        });
        
        // Reset form
        setInvitations([{ email: '', role: 'viewer' }]);
        setBatchName('');
        setGlobalMessage('');
        
        onInviteSuccess();
        onOpenChange(false);
      } else {
        throw new Error('Unexpected response from server');
      }
      
    } catch (error: any) {
      console.error('💥 Bulk invitation error:', error);
      
      toast({
        title: "Failed to send bulk invitations",
        description: error.message || 'Please try again later',
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Bulk Invite Users to Workspace
          </DialogTitle>
          <DialogDescription>
            Invite multiple users at once. You can add emails individually or paste a list.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Batch Settings */}
          <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
            <div className="space-y-2">
              <Label htmlFor="batch-name">Batch Name (Optional)</Label>
              <Input
                id="batch-name"
                placeholder="e.g., New Team Members, Q1 Partners"
                value={batchName}
                onChange={(e) => setBatchName(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="global-message">Global Message (Optional)</Label>
              <Textarea
                id="global-message"
                placeholder="Add a message that will be included in all invitations..."
                value={globalMessage}
                onChange={(e) => setGlobalMessage(e.target.value)}
                rows={2}
              />
            </div>
          </div>

          {/* Quick Add */}
          <div className="space-y-2">
            <Label>Quick Add Emails</Label>
            <Textarea
              placeholder="Paste multiple emails here (separated by commas, semicolons, or new lines)&#10;e.g., user1@example.com, user2@example.com&#10;user3@example.com"
              onChange={(e) => {
                if (e.target.value.trim()) {
                  parseEmailList(e.target.value);
                  e.target.value = '';
                }
              }}
              rows={3}
            />
          </div>

          {/* Individual Invitations */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Individual Invitations</Label>
              <Button type="button" variant="outline" size="sm" onClick={addInvitation}>
                <Plus className="h-4 w-4 mr-1" />
                Add Another
              </Button>
            </div>
            
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {invitations.map((invitation, index) => (
                <div key={index} className="flex items-start gap-2 p-3 border rounded-lg">
                  <div className="flex-1 space-y-2">
                    <div className="flex gap-2">
                      <Input
                        placeholder="user@example.com"
                        value={invitation.email}
                        onChange={(e) => updateInvitation(index, 'email', e.target.value)}
                        className="flex-1"
                      />
                      <Select 
                        value={invitation.role} 
                        onValueChange={(value) => updateInvitation(index, 'role', value)}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map((role) => (
                            <SelectItem key={role.value} value={role.value}>
                              {role.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      placeholder="Custom message for this user (optional)"
                      value={invitation.customMessage || ''}
                      onChange={(e) => updateInvitation(index, 'customMessage', e.target.value)}
                      className="text-sm"
                    />
                  </div>
                  {invitations.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeInvitation(index)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Badge variant="secondary">
              {invitations.filter(inv => inv.email.trim()).length} valid invitations
            </Badge>
            <Badge variant="outline">
              {Object.values(invitations.reduce((acc, inv) => {
                if (inv.role) acc[inv.role] = (acc[inv.role] || 0) + 1;
                return acc;
              }, {} as Record<string, number>)).join(', ')} roles
            </Badge>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleBulkInvite} disabled={isLoading}>
            {isLoading ? 'Sending...' : `Send ${invitations.filter(inv => inv.email.trim()).length} Invitations`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}